import React, { useEffect, useMemo, useState, useRef } from "react";
import NavBar from "../../utils/nav/NavBar";
import NavMenu from "../../utils/nav/NavMenu";
import Loading from "../../utils/loading";
import Authorization from "../../utils/secure/Authorization";
import { GetProductosNoVendidos } from "../../../services/reportesServices";
import { GetProducto, PutProducto } from "../../../services/productosServices";
import Modal from "../../utils/modal";
import Checkbox from "../../utils/checkBox";

// Helper to robustly extract product ID from a row coming from reportes API
const getProductoIdFromRow = (p) => {
  if (!p) return null;
  const cand = [
    p.ProductoId, // New field from reportes API (authoritative)
    p.id,
    p.idProducto,
    p.productoId,
    p.productoID,
    p.idProd,
    p.idprod,
    p.producto?.id,
    p.producto?.productoId,
    p.producto?.idProducto,
    p.idProductoNavigation?.id,
  ];
  const id = cand.find((v) => v !== undefined && v !== null && v !== "");
  return id ?? null;
};

const PAGE_SIZE = 25;

// Sorting helper: compare dates safely, nulls last
const compareUltimaVenta = (a, b, dir = 'desc') => {
  const da = a?.ultimaVenta ? new Date(a.ultimaVenta) : null;
  const db = b?.ultimaVenta ? new Date(b.ultimaVenta) : null;
  const av = da ? da.getTime() : null;
  const bv = db ? db.getTime() : null;
  // Nulls last for both directions
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (dir === 'asc') return av - bv;
  return bv - av; // desc
};

const ProductosNoVendidos = () => {
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const [diasSinVenta, setDiasSinVenta] = useState(60);
  const [search, setSearch] = useState("");
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const searchInputRef = useRef(null);

  // Modal state for toggling Activo/Agotado
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState(null); // { id, producto, activo, agotado }
  const [editFlags, setEditFlags] = useState({ activo: true, agotado: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initial load
    cargarDatos(diasSinVenta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async (dias) => {
    try {
      setLoading(true);
      const resp = await GetProductosNoVendidos(dias);
      if (!resp.exitoso && resp.mensajeError) {
        alert(resp.mensajeError);
        return;
      }
      // The API sample shows { productos: [...] }
      const data = resp.productos ?? resp;
      setProductos(Array.isArray(data) ? data : []);
      setPage(1);
        
    } catch (e) {
      console.error(e);
      alert(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = (search || "").toLowerCase();
    if (!term) return productos;
    return productos.filter((p) => (p.producto || "").toLowerCase().includes(term));
  }, [productos, search]);

  // Sorted by Última Venta based on current direction
  const sortedFiltered = useMemo(() => {
    // Copy to avoid mutating original array
    const arr = [...filtered];
    arr.sort((a, b) => compareUltimaVenta(a, b, sortDir));
    return arr;
  }, [filtered, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedFiltered.slice(start, start + PAGE_SIZE);
  }, [sortedFiltered, page]);

  const onDiasChange = (val) => {
    const n = Number(val);
    if (Number.isNaN(n)) return;
    setDiasSinVenta(n);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setModalOpen(false);
    setSelectedProducto(null);
  };

  const saveFlags = async () => {
    if (selectedProducto?.id == null) {
      alert("No se pudo identificar el producto a guardar.");
      return;
    }
    try {
      setSaving(true);
      // Load full product to avoid overwriting missing fields
      const fullResp = await GetProducto(selectedProducto.id);
      const full = fullResp?.producto ?? fullResp; // support either wrapped or direct response
      if (!full || full?.mensajeError) {
        throw new Error(full?.mensajeError || "No se pudo cargar el producto");
      }
      const productoToSave = {
        ...full,
        id: full.id ?? full.idProducto ?? full.productoId ?? selectedProducto.id,
        activo: !!editFlags.activo,
        agotado: !!editFlags.agotado,
      };
      const resp = await PutProducto(productoToSave);
      if (resp?.mensajeError && resp?.exitoso === false) {
        throw new Error(resp.mensajeError);
      }

      // Update local list flags for immediate feedback
      const id = productoToSave.id;
      setProductos((prev) => {
        const list = prev.map((x) => {
          const xid = x.ProductoId ?? x.id ?? x.idProducto ?? x.productoId;
          if (String(xid) === String(id)) return { ...x, activo: productoToSave.activo, agotado: productoToSave.agotado };
          return x;
        });
        return list;
      });

      closeModal(true);
    } catch (e) {
      console.error(e);
      alert(e?.message || e);
    } finally {
      setSaving(false);
    }
  };

  if (!permissionChecked)
    return (
      <div>
        <Authorization
          permission="Reportes/Productos sin ventas"
          onPermissionGranted={() => {
            setPermissionChecked(true);
            setPermissionGranted(true);
          }}
        />
      </div>
    );

  if (permissionChecked && !permissionGranted) return <div></div>;

  return (
    <div>
      <div className="page-header">
        <NavBar title="Productos sin ventas" navButtonComponent={<NavMenu />} />
        <div className="bar single margin-top-1" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div>
              <label style={{ fontSize: 12, display: "block", opacity: 0.8 }}>Días sin venta</label>
              <input
                type="number"
                min={0}
                value={diasSinVenta}
                onChange={(e) => onDiasChange(e.target.value)}
                style={{ width: 100 }}
              />
            </div>
            <button onClick={() => cargarDatos(diasSinVenta)} disabled={loading}>Cargar</button>
          </div>
          <div style={{ flex: 1, position: "relative", width: "100%" }}>
            <input
                className="search-input"
              type="text"
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ width: "100%", paddingRight: 28 }}
              ref={searchInputRef}
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); setPage(1); searchInputRef?.current && searchInputRef.current.focus(); }}
                title="Limpiar"
                aria-label="Limpiar búsqueda"
                style={{ position: "absolute", right: 4, top: "25%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "black", cursor: "pointer", fontSize: 16, opacity: 0.75, width: 28, height: 28, lineHeight: "28px", textAlign: "center", borderRadius: 4 }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="padding-1">
        <div className="list">
          <div className="list-item" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600, opacity: 0.8 }}>
            <div>Producto</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); setPage(1); }}
                title={`Ordenar por última venta (${sortDir === 'asc' ? 'ascendente' : 'descendente'})`}
                style={{ border: 'none', background: 'transparent', color: "black", cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <span>Última venta</span>
                <span aria-hidden="true" style={{ fontSize: 12, opacity: 0.9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
              </button>
              <div style={{ width: 80, textAlign: 'right' }}>Acciones</div>
            </div>
          </div>
          {pageItems.map((p, idx) => {
            const fecha = p.ultimaVenta ? new Date(p.ultimaVenta) : null;
            const ultima = fecha ? fecha.toLocaleDateString() : "Sin venta";
            const isInactiveOrAgotado = (p.activo === false) || (p.agotado === true);
            const rowStyle = {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              opacity: isInactiveOrAgotado ? 0.5 : 1
            };
            const idProducto = getProductoIdFromRow(p);
            return (
              <div key={idx} className="list-item" style={rowStyle}>
                <div style={{ fontWeight: 600 }}>{p.producto}</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ opacity: 0.8 }}>{ultima}</div>
                  <div style={{ width: 80, textAlign: 'right' }}>
                    <button
                      title="Editar estatus"
                      onClick={() => {
                        const id = getProductoIdFromRow(p);
                        if (id == null) {
                          alert("No se encontró el identificador del producto en esta fila.");
                          return;
                        }
                        setSelectedProducto({ id: id, nombre: p.producto });
                        setEditFlags({
                          activo: p.activo !== false, // default true
                          agotado: !!p.agotado,
                        });
                        setModalOpen(true);
                      }}
                    >
                      <i className="fas fa-pen" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {pageItems.length === 0 && !loading && (
            <div style={{ padding: 16, textAlign: "center", opacity: 0.7 }}>No hay datos</div>
          )}
        </div>

        {/* Footer: results and pagination */}
        <div className="bar single" style={{ display: "flex", alignItems: "center", marginTop: 8, justifyContent: "space-between", gap: 12 }}>
          <div>
            Resultados: {filtered.length} • Página {page} de {totalPages}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button disabled={page <= 1} onClick={() => setPage(1)}>{"<<"}</button>
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{"<"}</button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>{">"}</button>
            <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}>{">>"}</button>
          </div>
        </div>
      </div>

      {loading && <Loading />}

      {modalOpen && (
        <Modal contentStyle={{ maxWidth: 560, width: "100%", margin: "0 auto" }}>
          <Modal.Header closeButton onClose={closeModal}>
            <div className="div-between" style={{ width: "100%", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700 }}>Editar estatus</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>{selectedProducto?.nombre}</div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="grid gap-small">
              <Checkbox
                checked={!!editFlags.activo}
                label="Activo"
                onCheckedChanged={() => setEditFlags((f) => ({ ...f, activo: !f.activo }))}
              />
              <Checkbox
                checked={!!editFlags.agotado}
                label="Agotado"
                onCheckedChanged={() => setEditFlags((f) => ({ ...f, agotado: !f.agotado }))}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="div-right" style={{ width: "100%", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn" onClick={closeModal} disabled={saving}>Cancelar</button>
              <button type="button" className="btn primary" onClick={saveFlags} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default ProductosNoVendidos;
